import { Timeline } from '@arco-design/web-react'
import { useTranslation } from 'react-i18next'
import type { LogEntry } from '@/types'
import { parseLogOther } from '@/utils/parseLogOther'
import { renderDurationMs } from '@/utils/render'

interface LogTimelineProps {
  record: LogEntry
  isAdmin?: boolean
}

interface TimelineStep {
  key: string
  label: string
  ms?: number
}

export default function LogTimeline({ record, isAdmin = false }: LogTimelineProps) {
  const { t } = useTranslation()
  const parsed = parseLogOther(record.otherInfo)
  const timing = parsed?.timing

  const adminSteps: TimelineStep[] = [
    { key: 'auth', label: t('tt.log.timeline.auth'), ms: timing?.authMs },
    { key: 'route', label: t('tt.log.timeline.route'), ms: timing?.routeSelectMs },
    { key: 'pre_billing', label: t('tt.log.timeline.preBilling'), ms: timing?.preBillingMs },
    { key: 'upstream_ttfb', label: t('tt.log.timeline.upstreamTtfb'), ms: record.upstreamTtfbMs ?? timing?.upstreamTtfbMs },
    { key: 'client_write', label: t('tt.log.timeline.clientWrite'), ms: record.clientTtftMs ?? timing?.clientWriteMs ?? parsed?.stream?.clientTtftMs },
    { key: 'settle', label: t('tt.log.timeline.settle'), ms: timing?.settleBillingMs },
    { key: 'complete', label: t('tt.log.timeline.complete'), ms: record.totalMs ?? timing?.totalMs },
  ]

  const userSteps: TimelineStep[] = [
    {
      key: 'client_write',
      label: t('tt.log.timeline.clientWrite'),
      ms: record.clientTtftMs ?? timing?.clientWriteMs ?? parsed?.stream?.clientTtftMs,
    },
    {
      key: 'complete',
      label: t('tt.log.timeline.complete'),
      ms: record.totalMs ?? timing?.totalMs,
    },
  ]

  const steps = isAdmin ? adminSteps : userSteps.filter((s) => s.key !== 'client_write' || (record.stream ?? record.isStream))

  const hasAny = steps.some((s) => s.ms != null && s.ms > 0)
  if (!hasAny) {
    return <span className="text-gray-400 text-sm">-</span>
  }

  return (
    <Timeline className="!pl-0">
      {steps.map((step) => (
        <Timeline.Item key={step.key} label={step.label}>
          {renderDurationMs(step.ms)}
        </Timeline.Item>
      ))}
    </Timeline>
  )
}
